import crypto from 'node:crypto'
import fs from 'node:fs'
import path from 'node:path'
import { parseEnv } from 'node:util'
import type { EnvSection, ProjectEnvValue } from '@cli-types/core-engine/env.js'
import type { GeneratedFile } from '@cli-types/core-engine/schema.js'
import type { FileSnapshot } from '@cli-types/core-engine/utils.js'
import { DEVELOPMENT_ENV_FILES } from '@cli-constants/paths.js'
import { normalizeLineEndings } from '@cli-utils/text.js'
import fse from 'fs-extra'

/**
 * Walks from `startDir` to the filesystem root inclusive, returning the first
 * defined `visit` result. Inclusive matters: a walk that stops one level early
 * silently ignores a `package.json` or lockfile sitting at the root.
 */
export function findUpwards<T>(
  startDir: string,
  visit: (dir: string) => T | undefined
): T | undefined {
  let dir = path.resolve(startDir)

  for (;;) {
    const found = visit(dir)
    if (found !== undefined) return found

    const parent = path.dirname(dir)
    if (parent === dir) return undefined
    dir = parent
  }
}

export function hashContent(content: string): string {
  return `sha256-${crypto.createHash('sha256').update(normalizeLineEndings(content)).digest('hex')}`
}

export function ensureDir(dirPath: string): void {
  fse.ensureDirSync(dirPath)
}

export function safeWriteFile(filePath: string, content: string, force = false): boolean {
  if (!force && fs.existsSync(filePath)) {
    return false
  }
  ensureDir(path.dirname(filePath))
  fs.writeFileSync(filePath, content, 'utf-8')
  return true
}

export function safeCopyFile(src: string, dest: string, force = false): boolean {
  if (!force && fs.existsSync(dest)) {
    return false
  }
  ensureDir(path.dirname(dest))
  fs.copyFileSync(src, dest)
  return true
}

export function copyTemplateDir(
  srcDir: string,
  destDir: string,
  force = false
): { written: string[]; skipped: string[] } {
  const written: string[] = []
  const skipped: string[] = []

  function walk(currentSrc: string, currentDest: string): void {
    const entries = fs.readdirSync(currentSrc, { withFileTypes: true })
    for (const entry of entries) {
      const srcPath = path.join(currentSrc, entry.name)
      const destPath = path.join(currentDest, entry.name)

      if (entry.isDirectory()) {
        walk(srcPath, destPath)
      } else {
        if (safeCopyFile(srcPath, destPath, force)) {
          written.push(destPath)
        } else {
          skipped.push(destPath)
        }
      }
    }
  }

  walk(srcDir, destDir)
  return { written, skipped }
}

export function appendToFile(filePath: string, content: string, marker: string): boolean {
  if (fs.existsSync(filePath)) {
    const existing = fs.readFileSync(filePath, 'utf-8')
    if (existing.includes(marker)) {
      return false
    }
    fs.writeFileSync(filePath, `${existing.trimEnd()}\n${content}\n`, 'utf-8')
  } else {
    ensureDir(path.dirname(filePath))
    fs.writeFileSync(filePath, `${content}\n`, 'utf-8')
  }
  return true
}

/**
 * Snapshot manifests, skip lists, and conflict paths are all keyed by these
 * strings, so a Windows `\` here would make a project's snapshots unreadable on
 * any other machine.
 */
export function createGeneratedFile(filePath: string, content: string): GeneratedFile {
  return {
    path: filePath.split(path.sep).join('/'),
    content
  }
}

// Next.js and TypeScript both emit `tsconfig.json` with comments and trailing commas.
export function stripJsonComments(input: string): string {
  let result = ''
  let i = 0
  while (i < input.length) {
    if (input[i] === '"') {
      let j = i + 1
      while (j < input.length) {
        if (input[j] === '\\') {
          j += 2
          continue
        }
        if (input[j] === '"') {
          j++
          break
        }
        j++
      }
      result += input.slice(i, j)
      i = j
    } else if (input[i] === '/' && input[i + 1] === '/') {
      const nl = input.indexOf('\n', i)
      i = nl === -1 ? input.length : nl
    } else if (input[i] === '/' && input[i + 1] === '*') {
      const end = input.indexOf('*/', i + 2)
      i = end === -1 ? input.length : end + 2
    } else {
      result += input[i]
      i++
    }
  }
  return result
}

export function toStrictJson(input: string): string {
  return stripJsonComments(input).replace(/,\s*([\]}])/g, '$1')
}

/**
 * `node:util`'s parseEnv is the single owner of .env syntax here. A hand-rolled
 * splitter previously read `KEY="value"  # comment` back as `value"  # comment`
 * and understood neither `export` prefixes nor multi-line values.
 */
export function parseDotenvFile(filePath: string): Map<string, string> {
  if (!fs.existsSync(filePath)) return new Map()

  try {
    const parsed = Object.entries(parseEnv(fs.readFileSync(filePath, 'utf-8')))
    return new Map(
      parsed.filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    )
  } catch {
    return new Map()
  }
}

export function readEnvVar(cwd: string, key: string): string | undefined {
  return parseDotenvFile(path.join(cwd, '.env.local')).get(key)
}

/** Reads DEVELOPMENT_ENV_FILES in Next.js's development-file precedence order. */
export function resolveProjectEnvVar(cwd: string, key: string): ProjectEnvValue | undefined {
  for (const envFile of DEVELOPMENT_ENV_FILES) {
    const value = parseDotenvFile(path.join(cwd, envFile)).get(key)?.trim()
    if (value) return { source: envFile, value }
  }

  return undefined
}

export function readProjectEnvVar(cwd: string, key: string): string | undefined {
  return resolveProjectEnvVar(cwd, key)?.value
}

export function appendEnvVars(
  cwd: string,
  sections: EnvSection[],
  overwrite?: Set<string>
): { added: string[]; skipped: string[]; updated: string[] } {
  const envPath = path.join(cwd, '.env.local')
  let existing = fs.existsSync(envPath) ? fs.readFileSync(envPath, 'utf-8') : ''

  const existingKeys = new Set(
    existing
      .split('\n')
      .filter((line) => line.trim() && !line.trim().startsWith('#'))
      .map((line) => line.split('=')[0]?.trim())
      .filter(Boolean)
  )

  const added: string[] = []
  const skipped: string[] = []
  const updated: string[] = []
  const lines: string[] = []

  if (overwrite) {
    for (const section of sections) {
      for (const v of section.vars) {
        if (overwrite.has(v.key) && existingKeys.has(v.key)) {
          const pattern = new RegExp(`^${v.key}=.*$`, 'm')
          const replacement = v.comment
            ? `${v.key}="${v.value}"  # ${v.comment}`
            : `${v.key}="${v.value}"`
          existing = existing.replace(pattern, replacement)
          updated.push(v.key)
        }
      }
    }
  }

  if (existing.trim()) {
    lines.push('')
  }

  for (const section of sections) {
    const sectionVars = section.vars.filter((v) => {
      if (existingKeys.has(v.key)) {
        if (!updated.includes(v.key)) skipped.push(v.key)
        return false
      }
      added.push(v.key)
      return true
    })

    if (sectionVars.length === 0) continue

    lines.push(`# ${section.header}`)
    for (const v of sectionVars) {
      const line = v.comment ? `${v.key}="${v.value}"  # ${v.comment}` : `${v.key}="${v.value}"`
      lines.push(line)
    }
    lines.push('')
  }

  if (added.length > 0 || updated.length > 0) {
    const header = existing.trim()
      ? ''
      : '# ============================================\n# BetterStart Admin\n# ============================================\n'
    const content = existing.trim()
      ? `${existing.trimEnd()}\n${lines.join('\n')}`
      : header + lines.join('\n')
    fs.writeFileSync(envPath, content)
  }

  return { added, skipped, updated }
}

export function removeEnvVars(cwd: string, keys: string[]): string[] {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath) || keys.length === 0) {
    return []
  }

  const keySet = new Set(keys)
  const removed: string[] = []
  const filteredLines = fs
    .readFileSync(envPath, 'utf-8')
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith('#') || !trimmed.includes('=')) {
        return true
      }

      const [key] = trimmed.split('=')
      const normalizedKey = key?.trim()
      if (normalizedKey && keySet.has(normalizedKey)) {
        removed.push(normalizedKey)
        return false
      }

      return true
    })

  fs.writeFileSync(envPath, `${filteredLines.join('\n').trimEnd()}\n`, 'utf-8')
  return removed
}

export function captureFile(filePath: string): FileSnapshot {
  if (!fs.existsSync(filePath)) {
    return { existed: false }
  }

  return { existed: true, content: fs.readFileSync(filePath, 'utf-8') }
}

export function restoreFile(filePath: string, snapshot: FileSnapshot): void {
  if (snapshot.existed) {
    fs.writeFileSync(filePath, snapshot.content ?? '', 'utf-8')
    return
  }

  if (fs.existsSync(filePath)) {
    fs.rmSync(filePath, { force: true })
  }
}

export function readNonEmptyEnvVar(cwd: string, key: string): string | undefined {
  const value = readProjectEnvVar(cwd, key)?.trim()
  return value ? value : undefined
}
